import angular from 'angular';
import moment from 'moment';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import _round from 'lodash/round';
import { UPDATE_DEVICE, UPDATE_ORGANIZATION } from 'services/Permissions';
import { MEASUREMENT_SYSTEM_CHANGED_EVENT } from 'services/StudioEvents';
import { RANGE_LABEL_WEEK, SENSOR_GRAPH_RANGES, EXTENDED_SENSOR_GRAPH_RANGES, RANGE_LABEL_MONTH, TEMPERATURE_GRAPH_FILTERS, isPetGlycolFilterAvailable } from 'services/charting/constants';
import { getTemperatureUnitSuffix, getDifferentialPressureUnitSuffix, getDifferentialPressureDecimals, celsiusToFahrenheit, pascalToInchesOfWater, INCHES_OF_WATER_DECIMALS, isCelsiusField, hasOwnProperty, getHoursMinutesFormat, ruleTriggerDescription, ruleFields } from "../../../../services/utils";
import { States } from '../../../../app.router';
import { Duration } from './duration/duration.controller';
import phoneCallAudioUrl from '../../../../assets/audio/phoneCallExample.mp3';
import { getAvailableDynamicInputs } from 'services/alerts/dynamicInputs';

import AddDeviceDialogController from './add-device-dialog.controller';

const AddDeviceDialogTemplate = require('./add-device-dialog.html');

const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/* @ngInject */
export default class RulesDetailController {
    /**
     *
     * @param $state
     * @param $scope
     * @param RoleManager
     * @param RulesService
     * @param EventEmitter
     * @param {SensorService} SensorService
     * @param {ToastService} ToastService
     * @param {DialogService} DialogService
     */
    constructor($state, $scope, $rootScope, $transitions, $mdDialog, AuthService, RoleManager, RulesService, EventEmitter, SensorService, ToastService, DialogService, FeatureFlags, UserPreferencesManager, ProjectManager, IAMService) {
        this.$state = $state;
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$transitions = $transitions;
        this.$mdDialog = $mdDialog;
        this.AuthService = AuthService;
        this.RoleManager = RoleManager;
        this.RulesService = RulesService;
        this.EventEmitter = EventEmitter;
        this.SensorService = SensorService;
        this.ToastService = ToastService;
        this.DialogService = DialogService;
        this.FeatureFlags = FeatureFlags;
        this.UserPreferencesManager = UserPreferencesManager;
        this.ProjectManager = ProjectManager;
        this.IAMService = IAMService;
        this.fields = { ...ruleFields };
        if (!this.FeatureFlags.isActive('sensor_virtual_glycol_buffer')) {
            delete this.fields.virtualGlycolBuffer;
        }
        if (!this.FeatureFlags.isActive('notifications_battery_status')) {
            delete this.fields.batteryStatus;
        }
        this.dayOptions = dayOptions;
        /**
         * @type {Rule}
         */
        this.rule = undefined;
        this.triggerMode = undefined;

        // Contact groups support for organization templates
        this.contactGroups = [];

        this.userTimezone = null;
        if (typeof Intl !== "undefined") {
            this.userTimezone = Intl.DateTimeFormat?.().resolvedOptions().timeZone;
        }

        this.projectTimezone = this.ProjectManager.currentProject.location.timeLocation;
        this.scheduleTimezoneOption = null;

        this.showAdvancedOptions = false
        this.showPreview = false
        this.previewDevice = null

        this.onExitGuard = this.onExitGuard.bind(this);
        this.exampleAudio = phoneCallAudioUrl;
    }

    onExitGuard() {
        if (this.isEdited && !this.isNew) {
            return this.DialogService.confirm({
                title: 'Notification was edited',
                textContent: 'Do you really want to leave? Changes will not be saved.',
                ariaLabel: 'drop-changes',
                ok: 'Leave',
                cancel: 'Stay'
            })
                .then(() => true)
                .catch(() => false);
        }
        return true;
    }

    $onDestroy() {
        this.unregisterExitGuard();
    }

    $onInit() {

        // Convert field name for count based notifications (locally in Studio)
        if (this.rule.trigger?.field === 'objectPresent' && this.rule.trigger?.triggerCount) {
            this.rule.trigger.field = 'proximityCount'
        }
        if (this.rule.trigger?.field === 'touch' && this.rule.trigger?.triggerCount) {
            this.rule.trigger.field = 'touchCount'
        }
        if (this.rule.trigger?.field === 'contact' && this.rule.trigger?.triggerCount) {
            this.rule.trigger.field = 'contactCount'
        }

        // Check if this is an organization template
        this.isOrganizationTemplate = this.rule.isOrganizationTemplate || false;

        // Initialize scope for organization templates
        if (this.isOrganizationTemplate) {
            // Convert projectLabels to scope format for display
            if (!this.rule.scope) {
                this.rule.scope = {
                    type: 'ALL_PROJECTS',
                    labels: {}
                };

                // Check if there are project labels
                if (this.rule.projectLabels && Object.keys(this.rule.projectLabels).length > 0) {
                    this.rule.scope.type = 'PROJECT_LABELS';
                    this.rule.scope.labels = this.rule.projectLabels;
                }
            }
        }

        // Load contact groups for all rules (both organization templates and project rules)
        this.loadContactGroups();

        // BELOW and ABOVE range.type are added on the frontend as a convenience
        // These are not supported on the backend, OUTSIDE is the type that is actually sent on the API
        if (this.rule.trigger?.range?.type === 'OUTSIDE') {
            if (this.rule.trigger.range.lower !== null && this.rule.trigger.range.upper === null) {
                this.rule.trigger.range.type = 'BELOW';
            }
            if (this.rule.trigger.range.lower === null && this.rule.trigger.range.upper !== null) {
                this.rule.trigger.range.type = 'ABOVE';
            }
        }

        // Determine what deviceMode the rule is set to
        if (this.isNew) {
            this.rule.deviceMode = null
        } else if (this.rule.devices?.length > 0) {
            this.rule.deviceMode = 'SELECTED'
        } else if (this.rule.deviceLabels && Object.keys(this.rule.deviceLabels).length > 0) {
            this.rule.deviceMode = 'LABELS'
        } else {
            this.rule.deviceMode = 'ALL'
        }

        // Time options used to populate schedule input selection
        this.timeOptions = [];
        for (let hour = 0; hour < 24; hour++) {
            this.timeOptions.push({
                value: `${hour},${0}`,
                displayValue: moment({ hour }).format(getHoursMinutesFormat())
            })
            this.timeOptions.push({
                value: `${hour},${30}`,
                displayValue: moment({ hour, minute: 30 }).format(getHoursMinutesFormat())
            })
        }
        this.timeOptions.push({
            value: `${23},${59}`,
            displayValue: moment({ hour: 23, minute: 59 }).format(getHoursMinutesFormat())
        })

        const schedule = this.rule.schedule;
        if (schedule) {
            this.activeScheduleDays = [];

            // Convert time objects to string for use as ng-model for input selection
            // E.g. {hour: 8, minute: 0} is reduced to '8,0'
            // Converted back when saving notification
            schedule.slots.forEach(slot => {
                slot.times.forEach(time => {
                    time.start = `${time.start.hour},${time.start.minute}`
                    time.end = `${time.end.hour},${time.end.minute}`
                });
            });

            this.defaultScheduleStartTime = '8,0';
            this.defaultScheduleEndTime = '17,0';

            // Determine the current scheduleMode, EVERYDAY, WEEKDAYS or CUSTOM
            if (schedule.slots.length === 1 && schedule.slots[0]?.days.length >= 5) {
                const days = schedule.slots[0].days;
                this.activeScheduleDays = days;

                if (days.length === 7) {
                    this.scheduleMode = 'EVERYDAY'
                }

                if (days.length === 5 && !days.includes('Saturday') && !days.includes('Sunday')) {
                    this.scheduleMode = 'WEEKDAYS'
                }

                // Helping variables for EVERYDAY and WEEKDAYS schedule time options
                this.scheduleStartIndex = this.timeOptions.map(option => option.value).indexOf(schedule.slots[0].times[0].start);
                this.scheduleEndIndex = this.timeOptions.map(option => option.value).indexOf(schedule.slots[0].times[0].end);

            } else {

                this.scheduleMode = 'CUSTOM'
                // Helping array for all CUSTOM schedule time options
                // One element for each day
                this.customScheduleIndexes = [];
                this.dayOptions.forEach(() => {
                    this.customScheduleIndexes.push({ start: this.scheduleStartIndex, end: this.scheduleEndIndex })
                })
                schedule.slots.forEach(slot => {
                    this.activeScheduleDays.push(...slot.days)
                    const startIndex = this.timeOptions.map(option => option.value).indexOf(slot.times[0].start);
                    const endIndex = this.timeOptions.map(option => option.value).indexOf(slot.times[0].end);

                    const dayIndex = this.dayOptions.indexOf(slot.days[0]);
                    this.customScheduleIndexes[dayIndex].start = startIndex;
                    this.customScheduleIndexes[dayIndex].end = endIndex;
                });
            }

            // Initialize the timezone option based on current schedule settings
            this.initializeScheduleTimezoneOption();
        } else {
            // Initialize timezone option to null when no schedule exists
            this.scheduleTimezoneOption = null;
        }

        if (this.rule.escalationLevels.length === 0) {
            this.rule.escalationLevels.push({
                displayName: 'Escalation Level 1',
                actions: this.rule.actions,
                escalateAfter: '7200s'
            })
            this.rule.actions = []
        }

        // The API can leave out silent when it's false. The Sound switch only shows as on for an explicit false,
        // and isEdited compares JSON, so set it before the snapshot
        this.rule.escalationLevels
            .flatMap(level => level.actions?.filter(action => action.type === 'SIGNAL_TOWER') || [])
            .forEach(action => { action.signalTower.silent = action.signalTower.silent || false })

        this.originalRule = _cloneDeep(this.rule);

        this.unregisterExitGuard = this.$transitions.onExit({
            exiting: this.$state.current.name
        }, this.onExitGuard);

        if (!this.rule.triggerDelay) {
            this.triggerMode = "immediately";
        } else {
            this.triggerMode = "delay";
        }

        this.availableDynamicInputs = this.updateDynamicInputs();
        this.lastActionFocus = '';

        // Preview graph properties
        this.eventsObservable = null
        this.ranges = SENSOR_GRAPH_RANGES.filter(range => !EXTENDED_SENSOR_GRAPH_RANGES.includes(range));

        this.dataLoaded = false
        this.currentRange = RANGE_LABEL_WEEK

        this.deviceList = []
        this.changePreviewDevice = false

        this.glycolFilterEnabled = false
        if (this.rule.trigger?.range?.filter !== null && this.rule.trigger?.range?.filter !== undefined) {
            if (hasOwnProperty(this.rule.trigger?.range?.filter, 'productEquivalentTemperature')) {
                this.glycolFilterEnabled = true
            }
        }
        this.hasSavedPetGlycolFilter = this.glycolFilterEnabled

        // virtualGlycolBuffer takes precedence over the legacy PET simulation, same as
        // showPetGlycolFilter() in threshold-builder.
        if (this.rule.trigger?.field === 'virtualGlycolBuffer' && this.FeatureFlags.isActive('sensor_virtual_glycol_buffer')) {
            this.activeFilter = TEMPERATURE_GRAPH_FILTERS.VIRTUAL_GLYCOL_BUFFER
        } else if (this.glycolFilterEnabled) {
            this.activeFilter = TEMPERATURE_GRAPH_FILTERS.GLYCOL
        } else {
            this.activeFilter = TEMPERATURE_GRAPH_FILTERS.NONE
        }
        this.showAdvancedOptions = this.glycolFilterEnabled || this.rule.resolvedNotifications || this.rule.reminderNotifications

        this.invalidAcknowledgmentDuration = false
        if (this.rule.unacknowledgesAfter === null || this.isNew) {
            // Set the default value
            this.acknowledgmentDuration = {
                hours: 4,
                minutes: 0,
            }
        } else {
            // Existing rule with a set value
            const duration = Duration.fromString(this.rule.unacknowledgesAfter);
            this.acknowledgmentDuration = {
                hours: duration.hours,
                minutes: duration.minutes,
            }
        }

        // Get all signal tower actions so we can load the devices
        const signalTowerActions = this.rule.escalationLevels.flatMap(level => level.actions?.filter(action => action.type === 'SIGNAL_TOWER') || [])
        const signalTowerCloudConnectorIds = signalTowerActions.map(action => action.signalTower.cloudConnectorName.split('/')[3])
        this.signalTowerCloudConnectors = []
        if (signalTowerCloudConnectorIds.length > 0) {
            this.SensorService.getFromCache(signalTowerCloudConnectorIds).then(({ devices }) => {
                this.signalTowerCloudConnectors = devices
            })
        }

        this.$rootScope.$on(MEASUREMENT_SYSTEM_CHANGED_EVENT, () => {
            this.refreshDevicePreview() // Refresh graph to get correct C/F
        })

        // Get user email
        const authData = this.AuthService.getAuthData();
        if (authData) {
            this.userEmail = authData.profile.email
        }

        // Load contact groups for organization templates
        if (this.ruleIsOrganizationTemplate) {
            this.loadContactGroups();
        }

        // The webhook URL regex pattern. This is used to validate the webhook URL.
        this.webhookUrlRegex = /^https:\/\//;
    }

    rulesAreEqual(ruleA, ruleB) { // eslint-disable-line class-methods-use-this
        return _isEqual(angular.toJson(ruleA), angular.toJson(ruleB))
    }

    get isEdited() {
        return !this.rulesAreEqual(this.originalRule, this.rule)
    }

    get canCreate() {
        return this.RoleManager.can(UPDATE_DEVICE);
    }

    get canUpdate() {
        // TODO: Replace 'UPDATE_DEVICE' with a rules specific permission.
        return this.RoleManager.can(UPDATE_DEVICE) && !this.ruleIsOrganizationTemplate;
    }

    get canDelete() {
        // TODO: Replace 'UPDATE_DEVICE' with a rules specific permission.
        return this.RoleManager.can(UPDATE_DEVICE) && !this.ruleIsOrganizationTemplate;
    }

    get ruleIsOrganizationTemplate() {
        return this.rule && this.rule.isOrganizationTemplate;
    }

    get isOrganizationAdmin() {
        return this.RoleManager.can(UPDATE_ORGANIZATION);
    }

    get readyToSave() {
        if (this.ruleForm.$invalid) return false
        if (this.invalidAcknowledgmentDuration) return false
        if (!this.rule.trigger) return false
        if (this.rule.deviceMode === null) return false
        if (this.rule.deviceMode === 'SELECTED' && this.rule.devices?.length === 0) return false
        if (this.rule.deviceMode === 'LABELS' && this.rule.deviceLabels && Object.keys(this.rule.deviceLabels).length === 0) return false
        // if (this.rule.actions?.length === 0) return false
        if (this.rule.escalationLevels?.length === 0) return false
        if (this.rule.escalationLevels?.some(level => level.actions?.length === 0)) return false
        // Ensure there are no signal tower actions without a cloud connector
        if (this.rule.escalationLevels?.some(level => level.actions?.some(action => action.type === 'SIGNAL_TOWER' && !action.signalTower.cloudConnectorName))) return false
        return true
    }

    get lowerUpperThresholds() {
        let { lower, upper } = this.rule.trigger.range
        if (isCelsiusField(this.rule.trigger.field) && this.UserPreferencesManager.useFahrenheit) {
            lower = lower != null ? _round(celsiusToFahrenheit(lower), 1) : lower
            upper = upper != null ? _round(celsiusToFahrenheit(upper), 1) : upper
        }
        if (this.rule.trigger.field === 'differentialPressure' && this.UserPreferencesManager.useInchesOfWater) {
            lower = lower != null ? _round(pascalToInchesOfWater(lower), INCHES_OF_WATER_DECIMALS) : lower
            upper = upper != null ? _round(pascalToInchesOfWater(upper), INCHES_OF_WATER_DECIMALS) : upper
        }
        return `${lower},${upper}`
    }

    get previewDeviceNoData() {
        return moment().diff(moment(this.previewDevice?.lastSeen), 'days') > 31
    }

    get reminderFeatureFlag() {
        return this.FeatureFlags.isActive('notifications_reminder')
    }

    get defaultCorrigoWorkOrderDescription() {
        return `Created by a DT Sensor via API. DT Studio Dashboard: https://studio.disruptive-technologies.com/projects/${this.ProjectManager.currentProjectId}/dashboard`
    }

    get countStatusHelperText() {
        if (this.rule.trigger.field === 'proximityCount') {
            return this.rule.trigger.presence === 'PRESENT' ? 'closed events' : 'open events'
        }
        if (this.rule.trigger.field === 'contactCount') {
            return this.rule.trigger.contact === 'OPEN' ? 'open events' : 'closed events'
        }
        return 'touches'
    }

    get minTriggerDelay() {
        if (this.rule.trigger.field === 'connectionStatus' && this.rule.trigger.connection === 'SENSOR_OFFLINE') {
            return "900s" // 15 minutes
        }
        return "30s";
    }

    get maxTriggerDelay() {
        return "604800s" // 7 days
    }

    get minJoltTimeToComplete() {
        return "60s"; // 1 minute
    }

    get maxJoltTimeToComplete() {
        return "604800s"; // 7 days
    }

    get greatestRangeLabel() {
        return RANGE_LABEL_MONTH
    }

    // Returns true if a signal tower has been configured for the rule
    get resolvedNotificationCheckboxDisabled() {
        return this.rule?.escalationLevels?.flatMap(level => level.actions.filter(action => action.type === 'SIGNAL_TOWER'))?.length > 0
    }

    getCloudConnectorDisplayNameFromName(deviceName) {
        const foundDevice = this.signalTowerCloudConnectors.find(device => device.name === deviceName)
        return foundDevice?.labels.name || foundDevice?.id || deviceName
    }

    humanize(string) { // eslint-disable-line class-methods-use-this
        return Duration.humanizedFromString(string);
    }

    getOperatorByField(field) {
        return this.fields[field]?.operator;
    }

    getUnitByField(field) {
        // Show the correct fahrenheit/celsius unit if temperature, and
        // the default unit otherwise
        if (isCelsiusField(field)) {
            return getTemperatureUnitSuffix()
        }
        if (field === 'differentialPressure') {
            return getDifferentialPressureUnitSuffix()
        }

        return this.fields[field].unit;
    }

    getStepByField(field) {
        if (field === 'differentialPressure') {
            return 10 ** -getDifferentialPressureDecimals()
        }
        return 0.01
    }

    // Still shown for rules saved with it, so the setting stays visible and can be turned off
    showPetGlycolFilter() {
        return isPetGlycolFilterAvailable(this.FeatureFlags) || this.hasSavedPetGlycolFilter;
    }

    isPetGlycolFilterReplaced() {
        return !isPetGlycolFilterAvailable(this.FeatureFlags);
    }

    getDisplayOption(value) {
        return this.fields[this.rule.trigger.field].operatorDisplayNames[value];
    }

    triggerFieldChanged() {
        Object.keys(this.rule.trigger).forEach((key) => {
            switch (key) {
                case 'field':
                case this.fields[this.rule.trigger.field].operator:
                    break;
                default:
                    delete this.rule.trigger[key];
                    break;
            }
            if (this.fields[this.rule.trigger.field].displayName === 'Touched') {
                this.triggerMode = 'immediately'
                this.rule.triggerDelay = null
            }
        });

        this.showAdvancedOptions = false
        this.showPreview = false

        this.glycolFilterEnabled = false
        this.activeFilter = this.rule.trigger.field === 'virtualGlycolBuffer' && this.FeatureFlags.isActive('sensor_virtual_glycol_buffer')
            ? TEMPERATURE_GRAPH_FILTERS.VIRTUAL_GLYCOL_BUFFER
            : TEMPERATURE_GRAPH_FILTERS.NONE

        if (this.getOperatorByField(this.rule.trigger.field) === 'count') {
            delete this.rule.triggerDelay
            this.removeSchedule()
        }

        if (this.rule.trigger.field === 'batteryStatus') {
            this.triggerMode = 'immediately'
            this.rule.triggerDelay = null
        }

        // Set default from field
        this.setDefaultFieldOptions();
        this.availableDynamicInputs = this.updateDynamicInputs();

        if (this.rule.trigger.field === 'connectionStatus') {
            this.connectionStatusTypeChanged();
        } else {
            // If the trigger field was "connectionStatus", the minTriggerDelay could have been set to
            // 15 minutes. In this case, we need to run the trigger delay validation again.
            this.runTriggerDelayValidation();
        }
    }

    connectionStatusTypeChanged() {
        // If we're switching to sensor offline, make sure the trigger delay is at least 15 minutes.
        if (this.rule.trigger.connection === "SENSOR_OFFLINE") {
            if (this.triggerMode === "delay") {
                // The trigger mode was already set to "delay". Make sure the delay is at least 15 minutes.
                const delayMinutes = Duration.fromString(this.rule.triggerDelay).momentDuration.asMinutes();
                if (delayMinutes < 15) {
                    this.rule.triggerDelay = "900s";
                }
            } else {
                // The trigger mode was set to "immediate". Change it to a 15 minute delay.
                this.triggerMode = "delay";
                this.rule.triggerDelay = "900s";
            }
        }

        // minTriggerDelay could have changed, so we need to run the trigger delay validation again.
        this.runTriggerDelayValidation();
    }

    /**
     * Asynchronously runs validation on the trigger delay component. This is required if minTriggerDelay
     * or maxTriggerDelay changes, since the dt-duration component does not automatically re-validate when
     * these change.
     */
    runTriggerDelayValidation() {
        setTimeout(() => {
            this.ruleForm.$$controls.forEach(control => {
                if (control.$name === "triggerDelay") {
                    control.$validate();
                }
            })

            this.$scope.$applyAsync();
        }, 1);
    }

    triggerModeChanged() {
        if (this.triggerMode === "delay" && !this.rule.triggerDelay) {
            this.rule.triggerDelay = '300s';
        } else if (this.triggerMode === "immediately") {
            this.rule.triggerDelay = null;
        }
    }

    triggerRangeTypeChanged() {
        if (this.rule.trigger.range.type === 'ABOVE') {
            this.rule.trigger.range.lower = null;
        }
        if (this.rule.trigger.range.type === 'BELOW') {
            this.rule.trigger.range.upper = null;
        }
    }

    setDefaultFieldOptions() {
        Object.assign(this.rule.trigger, this.fields[this.rule.trigger.field].default);
        this.rule.reminderNotifications = false
        // Signal Tower actions require resolved notifications, and the checkbox is locked while one is configured
        this.rule.resolvedNotifications = this.resolvedNotificationCheckboxDisabled
    }

    // Trigger preview graph
    setInitialized() {
        this.dataLoaded = true
        this.$rootScope.$applyAsync()
    }

    handleChartSelection() {
        this.currentRange = null
    }

    setRange({ range }) {
        this.currentRange = range
    }

    onChangeDevices() {
        if (this.deviceList.length === 1) {
            this.previewDevice = this.deviceList[0].device
            this.refreshDevicePreview()
        }

        if (this.deviceList.length === 0) {
            this.previewDevice = null
            this.dataLoaded = false
        }
    }

    togglePreview() {
        this.showPreview = !this.showPreview
        if (!this.showPreview) {
            this.currentRange = RANGE_LABEL_WEEK
            this.dataLoaded = false
        }
        if (this.deviceList.length > 0) {
            this.previewDevice = this.deviceList[0].device
        }
    }

    refreshDevicePreview() {
        // Refresh preview device by quickly hiding the element with ng-if
        this.eventsObservable = null
        this.changePreviewDevice = true
        this.dataLoaded = false
        this.$rootScope.$applyAsync()
        setTimeout(() => {
            this.changePreviewDevice = false
            this.$rootScope.$applyAsync()
        }, 1);
    }

    glycolFilterChanged() {
        if (this.glycolFilterEnabled) {
            this.activeFilter = TEMPERATURE_GRAPH_FILTERS.GLYCOL
            this.rule.trigger.range.filter = {
                productEquivalentTemperature: {}
            }
        } else {
            this.activeFilter = TEMPERATURE_GRAPH_FILTERS.NONE
            this.rule.trigger.range.filter = null
        }
        this.refreshDevicePreview() // Update graph to use filter
    }

    acknowledgmentDurationChanged() {
        // Convert hours and minutes to seconds on string format e.g. '7200s'
        const duration = new Duration(0, this.acknowledgmentDuration.minutes, this.acknowledgmentDuration.hours);
        this.invalidAcknowledgmentDuration = duration.momentDuration.asSeconds() < 60 || duration.momentDuration.asSeconds() > 604800;
        if (!this.invalidAcknowledgmentDuration) {
            this.rule.unacknowledgesAfter = duration.toString();
        }
    }

    setStartIndex(day, time) {
        // Selected start index for schedule time options
        if (this.scheduleMode !== 'CUSTOM') {
            this.scheduleStartIndex = this.timeOptions.map(e => e.value).indexOf(time);
        } else {
            const dayIndex = this.dayOptions.indexOf(day);
            this.customScheduleIndexes[dayIndex].start = this.timeOptions.map(e => e.value).indexOf(time);
        }
    }

    setEndIndex(day, time) {
        // Selected end index for schedule time options
        if (this.scheduleMode !== 'CUSTOM') {
            this.scheduleEndIndex = this.timeOptions.map(e => e.value).indexOf(time);
        } else {
            const dayIndex = this.dayOptions.indexOf(day);
            this.customScheduleIndexes[dayIndex].end = this.timeOptions.map(e => e.value).indexOf(time);
        }
    }

    showAddSignalTowerCloudConnectorDialog(ev, action) {
        this.$mdDialog.show({
            controller: AddDeviceDialogController,
            template: AddDeviceDialogTemplate,
            parent: angular.element(document.body),
            targetEvent: ev,
            clickOutsideToClose: true,
            fullscreen: true,
            locals: {
                deviceTypes: 'ccon',
                selectedDevices: [],
                onAddDevice: (device) => this.onAddSignalTowerCloudConnector(action, device)
            },
            controllerAs: '$ctrl',
        });
    }

    onAddSignalTowerCloudConnector(action, device) {
        action.signalTower.cloudConnectorName = device.name;
        this.SensorService.getFromCache([device.name.split('/')[3]]).then(({ devices }) => {
            const newNames = devices.map(d => d.name)
            this.signalTowerCloudConnectors = [
                ...this.signalTowerCloudConnectors.filter(existing => !newNames.includes(existing.name)),
                ...devices,
            ]
        })
        this.$mdDialog.cancel();
    }

    removeSignalTowerCloudConnector(action) {
        action.signalTower.cloudConnectorName = '';
        this.signalTowerCloudConnectors = this.signalTowerCloudConnectors.filter(name => name !== this.getCloudConnectorDisplayNameFromName(action.signalTower.cloudConnectorName))
    }

    openCloudConnectorDetails(name) {
        this.$state.go(States.SENSOR_DETAILS, {
            projectId: name.split('/')[1],
            sensorId: name.split('/')[3]
        });
    }

    scheduleDayToggle(day) {

        const index = this.activeScheduleDays.indexOf(day);
        if (index !== -1) {
            // Remove schedule slot for give day
            this.activeScheduleDays.splice(index, 1);
            const slotIndex = this.rule.schedule.slots.map(slot => slot.days.includes(day)).indexOf(true);
            this.rule.schedule.slots.splice(slotIndex, 1);
        } else {
            // Add new slot for given day
            this.activeScheduleDays.push(day)
            this.rule.schedule.slots.push({
                days: [day],
                times: [{
                    start: this.defaultScheduleStartTime,
                    end: this.defaultScheduleEndTime
                }]
            })
        }
    }

    enableSchedule() {
        this.activeScheduleDays = this.dayOptions;
        let timezone = 'UTC'
        let useProjectTimeLocation = false;

        // Default to "Use Project Timezone" only if the project has a timeLocation configured
        if (this.projectTimezone !== '' && this.projectTimezone !== null) {
            timezone = '';  // Empty string when using project timezone
            useProjectTimeLocation = true;
            this.scheduleTimezoneOption = 'use-project-timezone';
        } else if (this.userTimezone !== null) {
            timezone = this.userTimezone
            this.scheduleTimezoneOption = this.userTimezone;
        } else {
            this.scheduleTimezoneOption = 'UTC';
        }

        this.rule.schedule = {
            inverse: false,
            timezone,
            useProjectTimeLocation
        };
        this.scheduleMode = 'EVERYDAY'
        this.scheduleModeChanged()
    }

    removeSchedule() {
        delete this.rule.schedule;
    }

    scheduleModeChanged() {

        // Reset schedule slots and helping variables
        this.rule.schedule.slots = [];
        this.defaultScheduleStartTime = '8,0';
        this.defaultScheduleEndTime = '17,0';
        this.scheduleStartIndex = this.timeOptions.map(e => e.value).indexOf(this.defaultScheduleStartTime);
        this.scheduleEndIndex = this.timeOptions.map(e => e.value).indexOf(this.defaultScheduleEndTime);

        // Set new mode with default values
        switch (this.scheduleMode) {
            case 'EVERYDAY':
                this.activeScheduleDays = this.dayOptions.slice(0);;
                this.rule.schedule.slots = [{
                    days: this.activeScheduleDays,
                    times: [{
                        start: this.defaultScheduleStartTime,
                        end: this.defaultScheduleEndTime
                    }]
                }]
                break;

            case 'WEEKDAYS':
                this.activeScheduleDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
                this.rule.schedule.slots = [{
                    days: this.activeScheduleDays,
                    times: [{
                        start: this.defaultScheduleStartTime,
                        end: this.defaultScheduleEndTime
                    }]
                }]
                break;

            case 'CUSTOM':
                this.activeScheduleDays = this.dayOptions.slice(0);
                this.customScheduleIndexes = [];
                this.dayOptions.forEach(day => {
                    this.rule.schedule.slots.push({
                        days: [day],
                        times: [{
                            start: this.defaultScheduleStartTime,
                            end: this.defaultScheduleEndTime
                        }]
                    })
                    this.customScheduleIndexes.push({ start: 0, end: this.timeOptions.length - 1 });
                });
                break;

            default:
                console.error(`Unknown Schedule mode: ${this.scheduleMode}`) // eslint-disable-line no-console
        }
    }

    toggleTimeZone() {
        this.rule.schedule.timezone = (this.rule.schedule.timezone === 'UTC' ? this.userTimezone : 'UTC');
    }

    initializeScheduleTimezoneOption() {
        if (this.rule.schedule.useProjectTimeLocation) {
            this.scheduleTimezoneOption = 'use-project-timezone';
        } else {
            this.scheduleTimezoneOption = this.rule.schedule.timezone;
        }
    }

    scheduleTimezoneOptionChanged() {
        if (this.scheduleTimezoneOption === 'use-project-timezone') {
            this.rule.schedule.useProjectTimeLocation = true;
            this.rule.schedule.timezone = '';
        } else {
            this.rule.schedule.useProjectTimeLocation = false;
            this.rule.schedule.timezone = this.scheduleTimezoneOption;
        }
    }

    updateDynamicInputs() {

        if (!this.rule.trigger) {
            return [];
        }

        return getAvailableDynamicInputs(this.rule.trigger.field, {
            reminderFeatureFlag: this.reminderFeatureFlag,
        });
    }

    insertDynamicInput(actionField, input) {

        // Appending input to the action
        if (!this.lastActionFocus) {
            this.lastActionFocus = 'body';
        }
        if (!actionField[this.lastActionFocus]) {
            actionField[this.lastActionFocus] = '';
        }
        actionField[this.lastActionFocus] += ` $${input} `;
    }

    hasCorrigoAction() {
        return this.FeatureFlags.isActive("notifications_corrigo");
    }

    hasServiceChannelAction() {
        return this.FeatureFlags.isActive("notifications_service_channel");
    }

    hasJoltListAction() {
        return this.FeatureFlags.isActive("notifications_jolt_list");
    }

    hasSMSAction() {
        return this.FeatureFlags.isActive("notifications_sms");
    }

    hasPhoneCallAction() {
        return this.FeatureFlags.isActive("notifications_phone_call");
    }

    hasWebhookAction() {
        return this.FeatureFlags.isActive("notifications_webhook");
    }

    hasSignalTowerAction() {
        return this.FeatureFlags.isActive("notifications_signal_tower");
    }

    addAction(escalation, type) {
        if (escalation === null) {
            this.rule.escalationLevels.push({
                displayName: 'Escalation Level 1',
                actions: [],
                escalateAfter: '7200s'
            });
            escalation = this.rule.escalationLevels[0]; // eslint-disable-line no-param-reassign
        }
        const action = { type };
        switch (type) {
            case 'SMS':
                action.sms = {
                    recipients: [],
                    contactGroups: [],
                    body: ''
                };
                break;
            case 'EMAIL':
                action.email = {
                    recipients: this.userEmail ? [this.userEmail] : [],
                    contactGroups: [],
                    subject: '',
                    body: ''
                };
                break;
            case 'PHONE_CALL':
                action.phoneCall = {
                    recipients: [],
                    contactGroups: [],
                    introduction: 'This is an automated call from Disruptive Technologies.',
                    message: ''
                };
                break;
            case 'WEBHOOK':
                action.webhook = {
                    url: '',
                    signatureSecret: '',
                    headers: {}
                }
                break;
            case 'CORRIGO':
                action.corrigo = {
                    assetId: '',
                    taskId: '',
                    customerId: '',
                    clientId: '',
                    clientSecret: '',
                    companyName: '',
                    subTypeId: '',
                    contactName: '',
                    contactAddress: '',
                    workOrderDescription: this.defaultCorrigoWorkOrderDescription
                };
                break;
            case 'SERVICE_CHANNEL':
                action.serviceChannel = {
                    storeId: '',
                    assetTagId: '',
                    trade: '',
                    description: ''
                };
                break;
            case 'SIGNAL_TOWER':
                action.signalTower = {
                    cloudConnectorName: '',
                    silent: false,
                };
                if (!this.rule.resolvedNotifications) {
                    this.rule.resolvedNotifications = true;
                    this.ToastService.showSimpleTranslated('alert_rule_resolved_enabled');
                }
                break;
            case 'JOLT_LIST':
                action.joltList = {
                    displayName: '',
                    listTemplateId: '',
                    locationId: '',
                    timeToComplete: '',
                    userName: '',
                    password: '',
                    appId: '',
                    companyId: '',
                }
                break;
            default:
                return;
        }
        escalation.actions.push(action);
        setTimeout(() => {
            window.scrollTo({
                top: document.getElementById("rules").scrollHeight,
                behavior: 'smooth'
            })
        }, 200);
    }

    removeAction(escalation, $index) {
        escalation.actions.splice($index, 1);
        // If there are no more actions, remove the escalation level
        if (escalation.actions.length === 0) {
            this.rule.escalationLevels.splice(this.rule.escalationLevels.indexOf(escalation), 1);
        }
    }

    hasActions(escalation) {
        if (escalation.actions === undefined) {
            return false;
        }
        return escalation.actions.length > 0;
    }

    addEscalationLevel() {
        this.rule.escalationLevels.push({
            displayName: `Escalation Level ${this.rule.escalationLevels.length + 1}`,
            actions: [],
            escalateAfter: '7200s'
        });
        setTimeout(() => {
            window.scrollTo({
                top: document.getElementById("rules").scrollHeight,
                behavior: 'smooth'
            })
        }, 200);
    }

    removeEscalationLevel(index) {
        this.rule.escalationLevels.splice(index, 1);
    }

    escalationTime(escalation) {
        const index = this.rule.escalationLevels.indexOf(escalation);
        if (index === 0) {
            if (this.rule.triggerDelay) {
                return Duration.humanizedFromString(this.rule.triggerDelay);
            }
            return 'immediately';
        }
        return ""
    }

    escalationDescription(escalation) {
        // Get index of escalation level
        const index = this.rule.escalationLevels.indexOf(escalation);
        if (index === 0) {
            return `Will be sent when ${ruleTriggerDescription(this.rule)}`
        }
        const humanizedDelay = Duration.humanizedFromString(this.rule.escalationLevels[index - 1].escalateAfter);
        return `Will be sent ${humanizedDelay} after the previous escalation if the alert remains unacknowledged, unaddressed, or unresolved.`;
    }

    updateRuleForBackendRangeType(r) { // eslint-disable-line class-methods-use-this
        if (r.trigger.range?.type === 'ABOVE' || r.trigger.range?.type === 'BELOW') {
            r.trigger.range.type = 'OUTSIDE';
        }
    }

    updateScheduleTimeSlots(r) { // eslint-disable-line class-methods-use-this
        // Convert string format back to time objects before saving notification
        r.schedule?.slots?.map(slot => { // eslint-disable-line
            slot?.times.map(time => { // eslint-disable-line
                const start = time.start.split(',')
                const end = time.end.split(',')
                time.start = {
                    hour: Number(start[0]),
                    minute: Number(start[1]),
                }
                time.end = {
                    hour: Number(end[0]),
                    minute: Number(end[1]),
                }
            });
        });
    }

    prepareRuleForSave() {
        const ruleToBeSaved = _cloneDeep(this.rule);

        this.updateRuleForBackendRangeType(ruleToBeSaved);

        this.updateScheduleTimeSlots(ruleToBeSaved);

        this.updateCountRuleForBackend(ruleToBeSaved)

        return ruleToBeSaved;
    }

    updateCountRuleForBackend(ruleToBeSaved) {
        // Convert field names for counting back to the expected API names + potentially reset current count for devices
        if (this.rule.trigger.triggerCount) {
            if (this.rule.trigger.field === 'proximityCount') {
                ruleToBeSaved.trigger.field = 'objectPresent'
            }
            if (this.rule.trigger.field === 'touchCount') {
                ruleToBeSaved.trigger.field = 'touch'
            }
            if (this.rule.trigger.field === 'contactCount') {
                ruleToBeSaved.trigger.field = 'contact'
            }

            // Reset currentCount for devices if trigger has been updated
            if (!this.isNew) {
                if (this.rule.trigger.field === 'proximityCount') {
                    if (this.originalRule.trigger.presence !== ruleToBeSaved.trigger.presence) {
                        this.resetCurrentCountForDevices()
                    }
                }
                if (this.rule.trigger.field === 'contactCount') {
                    if (this.originalRule.trigger.contact !== ruleToBeSaved.trigger.contact) {
                        this.resetCurrentCountForDevices()
                    }
                }
                if (this.originalRule.trigger.triggerCount !== ruleToBeSaved.trigger.triggerCount) {
                    this.resetCurrentCountForDevices()
                }
            }
        }
    }

    resetCurrentCountForDevices() {
        this.deviceList.forEach(device => {
            device.currentCount = 0
        });
    }

    createRule() {
        this.RulesService.saveRule(this.prepareRuleForSave())
            .then(
                (rule) => {
                    this.onCreate(this.EventEmitter({ rule }));
                    this.$state.go(States.ALERTS_RULES_DETAIL, {
                        ruleId: rule.id,
                        rule
                    });
                }
            )
            .then(() => {
                this.ToastService.showSimpleTranslated('alert_rule_was_created', { position: 'bottom right' })
                this.$state.go(States.ALERTS_RULES)
            })
            .catch(serverResponse => {
                this.ToastService.showSimpleTranslated('alert_rule_wasnt_created', {
                    serverResponse
                })
            })
    }

    updateRule() {
        this.RulesService.saveRule(this.prepareRuleForSave())
            .then(
                rule => this.onUpdate(this.EventEmitter({ rule }))
            )
            .then(() => {
                this.ToastService.showSimpleTranslated('alert_rule_was_updated', { position: 'bottom right' })
                this.originalRule = _cloneDeep(this.rule);
            })
            .catch(serverResponse => {
                this.ToastService.showSimpleTranslated('alert_rule_wasnt_updated', {
                    serverResponse
                })
            })
    }

    deleteRule() {
        this.DialogService.confirm({
            title: 'Delete Alert Rule',
            textContent: 'Do you really want to delete the alert rule?',
            ok: 'Delete',
            cancel: 'Cancel'
        })
            .then(() => {
                this.RulesService.deleteRule(this.rule.id)
                    .then(() => {
                        this.ToastService.showSimpleTranslated('alert_rule_was_deleted');
                        this.onDelete(this.EventEmitter({ ruleId: this.rule.id }));
                        this.$state.go(States.ALERTS_RULES);
                    })
                    .catch(() => this.ToastService.showSimpleTranslated('alert_rule_wasnt_deleted'));

            })
            .catch(() => false);
    }

    goToOrganizationTemplate() {
        // Navigate to organization alert config with the template selected
        // The rule name format is: organizations/{orgId}/rules/{ruleId}
        const organizationId = this.rule.name.split('/')[1];

        this.$state.go('main.projects.project.organization-alert-config', {
            organizationId: organizationId,
            selectedTemplateName: this.rule.name
        });
    }

    // Contact Groups Methods
    async loadContactGroups() {
        // Check if IAMService is available
        if (!this.IAMService) {
            // eslint-disable-next-line no-console
            console.warn('IAMService not available for loading contact groups');
            this.contactGroups = [];
            return;
        }

        let organizationId = null;

        // For organization templates, get organization ID from the rule name
        if (this.rule && this.rule.name && this.rule.name.startsWith('organizations/')) {
            organizationId = this.rule.name.split('/')[1];
        }
        // For project rules, get organization ID from the current project
        else if (this.ProjectManager && this.ProjectManager.currentProject) {
            organizationId = this.ProjectManager.currentProject.organizationId;
        }

        if (!organizationId) {
            // eslint-disable-next-line no-console
            console.warn('No organization ID available for loading contact groups');
            this.contactGroups = [];
            return;
        }

        try {
            const response = await this.IAMService.listContactGroups(organizationId);
            this.contactGroups = response.contactGroups || [];
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to load contact groups:', error);
            this.contactGroups = [];
        }
        this.$scope.$applyAsync();
    }

    getContactGroupDisplayName(resourceName) {
        const contactGroup = this.contactGroups ? this.contactGroups.find(cg => cg.name === resourceName) : null;
        return contactGroup ? contactGroup.displayName : resourceName;
    }

    getContactGroupDescription(resourceName) {
        const contactGroup = this.contactGroups ? this.contactGroups.find(cg => cg.name === resourceName) : null;
        return contactGroup ? contactGroup.description : '';
    }

    addContactGroupToAction(action, contactGroupName) {
        // Map action types to their corresponding property names
        const actionTypeMap = {
            'EMAIL': 'email',
            'SMS': 'sms',
            'PHONE_CALL': 'phoneCall'
        };

        const propertyName = actionTypeMap[action.type];
        if (!propertyName) return;

        const actionObject = action[propertyName];
        if (!actionObject) return;

        if (!actionObject.contactGroups) {
            actionObject.contactGroups = [];
        }
        if (!actionObject.contactGroups.includes(contactGroupName)) {
            actionObject.contactGroups.push(contactGroupName);
        }
    }

    removeContactGroupFromAction(action, contactGroupName) {
        // Map action types to their corresponding property names
        const actionTypeMap = {
            'EMAIL': 'email',
            'SMS': 'sms',
            'PHONE_CALL': 'phoneCall'
        };

        const propertyName = actionTypeMap[action.type];
        if (!propertyName) return;

        const actionObject = action[propertyName];
        if (!actionObject) return;

        if (actionObject.contactGroups) {
            const index = actionObject.contactGroups.indexOf(contactGroupName);
            if (index > -1) {
                actionObject.contactGroups.splice(index, 1);
            }
        }
    }

    // Project scope information for organization templates
    getProjectScopeDescription() {
        if (!this.rule || !this.rule.projectLabels) {
            return 'All projects in the organization';
        }

        const labelCount = Object.keys(this.rule.projectLabels).length;
        if (labelCount === 0) {
            return 'All projects in the organization';
        }

        const labels = Object.entries(this.rule.projectLabels).map(([key, value]) =>
            value ? `${key}=${value}` : key
        ).join(', ');

        return `Projects with labels: ${labels}`;
    }
}
